import React from "react";
import {
    Box,
    SimpleGrid,
    Heading,
    Text,
    useColorModeValue,
    useDisclosure
} from "@chakra-ui/react";
import DataTableCard from "../dataTables/components/DataTableCard";
import InAppPromotionsModal from "../dataTables/components/InAppPromotionsModal";
import BonusModal from "../dataTables/components/BonusModal";
import TransfersSummaryModal from "../dataTables/components/TransfersSummaryModal";
import BlockedUnregisteredUsersModal from "../dataTables/components/BlockedUnregisteredUsersModal";
import WhatsappOptOutsModal from "../dataTables/components/WhatsappOptOutsModal";

import { MdCampaign, MdCardGiftcard, MdAssessment, MdBlock, MdBugReport, MdPeople, MdCancel, MdInsights } from "react-icons/md";
import ShiftDebuggerModal from "./ShiftDebuggerModal";
import BulkStatusChangeModal from "./BulkStatusChangeModal";
import EmployeeStatusModal from "./EmployeeStatusModal";
import User from "../../../api/user";

export default function Tools() {
    const textColor = useColorModeValue("secondaryGray.900", "white");
    const isSuperAdmin = User.GetInfo()?.type === 1;

    const {
        isOpen: isInAppPromotionsOpen,
        onOpen: onInAppPromotionsOpen,
        onClose: onInAppPromotionsClose
    } = useDisclosure();

    const {
        isOpen: isBonusOpen,
        onOpen: onBonusOpen,
        onClose: onBonusClose
    } = useDisclosure();

    const {
        isOpen: isTransfersSummaryOpen,
        onOpen: onTransfersSummaryOpen,
        onClose: onTransfersSummaryClose
    } = useDisclosure();

    const {
        isOpen: isBlockedUnregisteredOpen,
        onOpen: onBlockedUnregisteredOpen,
        onClose: onBlockedUnregisteredClose
    } = useDisclosure();

    const {
        isOpen: isShiftDebuggerOpen,
        onOpen: onShiftDebuggerOpen,
        onClose: onShiftDebuggerClose
    } = useDisclosure();

    const {
        isOpen: isBulkStatusOpen,
        onOpen: onBulkStatusOpen,
        onClose: onBulkStatusClose
    } = useDisclosure();

    const {
        isOpen: isOptOutsOpen,
        onOpen: onOptOutsOpen,
        onClose: onOptOutsClose
    } = useDisclosure();

    const {
        isOpen: isEmployeeStatusOpen,
        onOpen: onEmployeeStatusOpen,
        onClose: onEmployeeStatusClose
    } = useDisclosure();

    const tools = [
        {
            id: "in-app-promotions",
            title: "In-App Promotions",
            description: "צפייה בפרומושנים פעילים באפליקציה",
            icon: MdCampaign,
            color: "pink",
            onClick: onInAppPromotionsOpen
        },
        {
            id: "bonuses",
            title: "הטבות חודשיות",
            description: "ניהול בונוסים לעובדים - טווח תאריכים, קריטריון זכאות וסוג בונוס",
            icon: MdCardGiftcard,
            color: "yellow.500",
            onClick: onBonusOpen,
            buttonText: "הצג הטבות"
        },
        {
            id: "transfers-summary",
            title: "סיכום העברות לפי חברה",
            description: "מספר בקשות ומשתמשים ייחודיים לפי חברה בטווח תאריכים",
            icon: MdAssessment,
            color: "cyan",
            onClick: onTransfersSummaryOpen
        },
        {
            id: "blocked-unregistered-users",
            title: "משתמשים חסומים לא רשומים",
            description: "ניהול עובדים שחסמו מהרשמה לאפליקציה",
            icon: MdBlock,
            color: "red",
            onClick: onBlockedUnregisteredOpen
        },
        {
            id: "shift-debugger",
            title: "בדיקת משמרות לעובד",
            description: "אבחון מדוע עובד לא רואה משמרות באפליקציה — בדיקת פייפליין, אלפון, קובץ נוכחות ותאימות Synerion",
            icon: MdBugReport,
            color: "purple",
            onClick: onShiftDebuggerOpen,
            buttonText: "בדוק עובד"
        },
        {
            id: "bulk-status-change",
            title: "שינוי סטטוס בכמות",
            description: "העלאת קובץ Excel עם עובדים ושינוי סטטוסם בבת אחת — לפי טלפון או ת.ז.",
            icon: MdPeople,
            color: "teal",
            onClick: onBulkStatusOpen,
            buttonText: "שנה סטטוס"
        },
        ...(isSuperAdmin ? [{
            id: "whatsapp-opt-outs",
            title: "רשימת הסרה - WhatsApp",
            description: "מספרים שביקשו להסיר אותם מרשימת הדיוור (הסר/STOP). ניהול הוספה וביטול.",
            icon: MdCancel,
            color: "orange",
            onClick: onOptOutsOpen,
            buttonText: "הצג רשימה"
        }] : []),
        ...(isSuperAdmin ? [{
            id: "employee-status",
            title: "סטטוס עובדים בחברות",
            description: "סה״כ באלפון, פעילים (משמרת ב-60 יום), רשומים, ביצעו העברה — פאנל אונבורדינג לכל חברה.",
            icon: MdInsights,
            color: "blue",
            onClick: onEmployeeStatusOpen,
            buttonText: "הצג סטטוס"
        }] : []),
    ];

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
            <Box mb="20px">
                <Heading size="lg" color={textColor} mb="4">
                    כלים
                </Heading>
                <Text color="secondaryGray.600" fontSize="md">
                    כלי ניהול ותפעול
                </Text>
            </Box>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing="20px"
                mb="20px"
            >
                {tools.map((tool) => (
                    <DataTableCard
                        key={tool.id}
                        title={tool.title}
                        description={tool.description}
                        icon={tool.icon}
                        color={tool.color}
                        onClick={tool.onClick}
                        buttonText={tool.buttonText}
                    />
                ))}
            </SimpleGrid>

            <InAppPromotionsModal
                isOpen={isInAppPromotionsOpen}
                onClose={onInAppPromotionsClose}
            />
            <BonusModal
                isOpen={isBonusOpen}
                onClose={onBonusClose}
            />
            <TransfersSummaryModal
                isOpen={isTransfersSummaryOpen}
                onClose={onTransfersSummaryClose}
            />
            <BlockedUnregisteredUsersModal
                isOpen={isBlockedUnregisteredOpen}
                onClose={onBlockedUnregisteredClose}
            />
            <ShiftDebuggerModal
                isOpen={isShiftDebuggerOpen}
                onClose={onShiftDebuggerClose}
            />
            <BulkStatusChangeModal
                isOpen={isBulkStatusOpen}
                onClose={onBulkStatusClose}
            />
            {isSuperAdmin && (
                <WhatsappOptOutsModal
                    isOpen={isOptOutsOpen}
                    onClose={onOptOutsClose}
                />
            )}
            {isSuperAdmin && (
                <EmployeeStatusModal
                    isOpen={isEmployeeStatusOpen}
                    onClose={onEmployeeStatusClose}
                />
            )}
        </Box>
    );
}
